
















































































































import { Vue, Component, Mixins, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { OrderGetters, OrderActions } from "@/store/modules/order/types";
import { GlobalGetters } from "@/store/modules/global/types";
import { LineItem, Order, OrderDetails } from "@/models/order/order";
import { Mobile } from "@/mixins/mobile";
import { CreatedType } from "@/models/order/helpers";
@Component
export default class OrderSummary extends Mixins(Mobile) {
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetOrder))
  storeOrder!: Order;
  @(namespace(StoreModule.Order).Action(OrderActions.AddCostDetails))
  addCostDetails!: (value: OrderDetails["cost_details"]) => Promise<void>;
  @(namespace(StoreModule.Order).Getter(OrderGetters.GetAssignedCustomer))
  customer!: string;
  @(namespace(StoreModule.Global).Getter(GlobalGetters.GetRole))
  userRole!: string;
  @(namespace(StoreModule.Order).Action(OrderActions.ClearCreatedType))
  clearCreatedType!: () => Promise<void>;

  protected showShippingInput = false;
  protected shippingText = "";
  protected shipping = 0;
  protected showTaxInput = false;
  protected taxText = "";
  protected tax = 0;
  protected requestedDateStr = "";
  protected updatedDateStr = "";
  protected expireDateStr = "";
  protected costDetails: OrderDetails["cost_details"] = {
    shipping: null,
    tax: null,
    freight_request: {
      addressType: null,
      services: null,
      shippingZip: null,
      requestDate: null,
      updatedDate: null
    }
  };

  async created() {
    if (this.storeOrder.created_type !== CreatedType.Duplicated && this.storeOrder.cost_details) {
      this.costDetails = Object.assign({},{...this.storeOrder.cost_details});
      if (this.storeOrder.cost_details.shipping !== null) {
        this.shippingText = this.storeOrder.cost_details.shipping.toString();
        this.shipping = this.storeOrder.cost_details.shipping;
      }
      if (this.storeOrder.cost_details.tax !== null) {
        this.taxText = this.storeOrder.cost_details.tax.toString();
        this.tax = this.storeOrder.cost_details.tax;
      }
      this.requestedDateStr = this.costDetails.freight_request?.requestDate !== null ? this.makeReadableDate(String(this.costDetails.freight_request?.requestDate)) : "";
      this.updatedDateStr = this.costDetails.freight_request?.updatedDate !== null ? this.makeReadableDate(String(this.costDetails.freight_request?.updatedDate)) : "";
      this.expireDateStr = this.costDetails.freight_request?.updatedDate !== null ? this.makeReadableDate(String(this.costDetails.freight_request?.updatedDate), true) : "";
    } else {
      this.shippingText = "";
      this.shipping = 0;
      this.taxText = "";
      this.tax = 0;
      await this.updateCostDetails();
      if (this.storeOrder.created_type === CreatedType.Duplicated) { // keeps any changed cost_details when moving throughout the quote submission pages
        await this.clearCreatedType();
      }
    }
  }

  protected makeReadableDate(savedDateStr: string, isExpirationDate = false): string {
    let savedDate = new Date(savedDateStr);
    savedDate = isExpirationDate ? new Date(savedDate.getTime() + 14 * 24 * 60 * 60 * 1000) : savedDate; // freight estimates expire in 2 weeks (14 days)
    return savedDate.toLocaleDateString("en-US");
  }

  protected async updateCostDetails(): Promise<void> {
  if (
    this.costDetails &&
    this.costDetails.freight_request &&
    this.isAdmin
  ) {
    this.costDetails.freight_request.updatedDate = new Date();
  }
  await this.addCostDetails(this.costDetails);
  }

  protected toggleShippingInput(): void {
    if (this.showShippingInput) {
      if (this.isShippingNumeric) {
        const shipCost = parseFloat(this.shippingText.valueOf());
        if (this.costDetails) {
          this.costDetails.shipping = shipCost;
        }
        this.shipping = shipCost;
      } else {
        this.shipping = 0;
        if (this.costDetails) {
          this.costDetails.shipping = null;
        }
      }
      this.updateCostDetails();
    }
    this.showShippingInput = !this.showShippingInput;
  }

  protected toggleTaxInput(): void {
    if (this.showTaxInput) {
      if (this.isTaxNumeric) {
        const taxCost = parseFloat(this.taxText.valueOf());
        if (this.costDetails) {
          this.costDetails.tax = taxCost;
        }
        this.tax = taxCost;
      } else {
        this.tax = 0;
        if (this.costDetails) {
          this.costDetails.tax = null;
        }
      }
      this.updateCostDetails();
    }
    this.showTaxInput = !this.showTaxInput;
  }

  get isAdmin(): boolean {
    return this.userRole === 'admin';
  }
  
  get showFreightRequestDate(): boolean {
    const requestDate = Date.parse(this.requestedDateStr);
    const updatedDate = Date.parse(this.updatedDateStr);
    if (
      !isNaN(requestDate) &&
      (
        isNaN(updatedDate) ||
        (
          !isNaN(updatedDate) &&
          requestDate > updatedDate
        )
      )
    ){
      return true;
    } else {
      return false;
    }
  }

  get showFreightUpdatedDate(): boolean {
    const requestDate = Date.parse(this.requestedDateStr);
    const updatedDate = Date.parse(this.updatedDateStr);
    if (
      !isNaN(updatedDate) &&
      !isNaN(requestDate) &&
      ( updatedDate! >= requestDate! )
    ){
      return true;
    } else {
      return false;
    }
  }

  get isShippingNumeric(): boolean {
    const num = parseFloat(this.shippingText);
    if (!isNaN(num)) {
      return true;
    } else {
      if (this.shippingText !== "") this.shippingText = "";
      return false;
    }
  }

  get isTaxNumeric(): boolean {
    const num = parseFloat(this.taxText);
    if (!isNaN(num)) {
      return true;
    } else {
      if (this.taxText !== "") this.taxText = "";
      return false;
    }
  }

  get price(): number {
    let price = 0;
    for (const item of this.storeOrder.items!) {
      price += item.qty * (item.unit_price * item.multiplier);
    }
    return price;
  }

  get totalPrice(): number {
    return (this.isAdmin || this.storeOrder.cost_details?.freight_request?.updatedDate !== null) ? this.price + this.shipping + this.tax : this.price;
  }

  get totalQty(): number {
    let qty = 0;
    if (this.storeOrder.items && this.storeOrder.items.length) {
      for (const item of this.storeOrder.items) {
        qty += item.qty;
      }
    }
    return qty;
  }
}
